import { Inject, Injectable, LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CommonModule, CurrencyPipe, DatePipe, registerLocaleData } from '@angular/common';
import { NgbConfig, NgbModule, NgbPopoverConfig } from '@ng-bootstrap/ng-bootstrap';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { NgSelectModule } from '@ng-select/ng-select';
import { IgniteModule } from '@ignite/angular';
import '@angular/localize/init';
/* i18n stuff */
import {
  TranslateLoader,
  TranslateModule,
  TranslateService
} from '@ngx-translate/core';

// http://www.localeplanet.com/java/index.html
import localeFr from '@angular/common/locales/fr';
import localeEs from '@angular/common/locales/es';
import localePortugese from '@angular/common/locales/pt';
import localeDeutsch from '@angular/common/locales/de';
import localeNederlands from '@angular/common/locales/nl';
import localeItaliano from '@angular/common/locales/it';
import localeMelayu from '@angular/common/locales/ms';
import localePilipino from '@angular/common/locales/fil';
import localeDansk from '@angular/common/locales/da';
import localeSvenska from '@angular/common/locales/sv';

registerLocaleData(localeFr);
registerLocaleData(localeEs);
registerLocaleData(localePortugese);
registerLocaleData(localeDeutsch);
registerLocaleData(localeNederlands);
registerLocaleData(localeItaliano);
registerLocaleData(localeMelayu);
registerLocaleData(localePilipino);
registerLocaleData(localePilipino, 'fl-PH');
registerLocaleData(localeDansk);
registerLocaleData(localeSvenska);

/* Our custom components and pipes */
import { UltiCurrencyPipe } from './pipes/ulticurrency.pipe';
import { UltiShortDatePipe } from './pipes/ultishortdate.pipe';
import { TranslateListPipe } from './pipes/translate-list.pipe';
import { KeysPipe } from './pipes/keys.pipe';
import { EnumTranslatePipe } from './pipes/enumtranslate.pipe';
import { IsMinDatePipe } from './pipes/ismindate.pipe';
import { VerticalProgressMapComponent } from './components/vertical-progress-map/vertical-progress-map.component';
import { VerticalProgressStepComponent } from './components/vertical-progress-step/vertical-progress-step.component';
import { ExpanderComponent } from './components/expander/expander.component';
import { MultiSelectComponent } from './components/multi-select/multi-select.component';
import { MultiTextComponent } from './components/multi-select/multi-text.component';
import { DatePickerComponent } from './components/date-picker/date-picker.component';
import {
  MultiSelectListItemTemplateDirective,
  MultiSelectSelectedItemTemplateDirective
} from './components/multi-select/multi-select-templates.directive';
import { UltiTranslateLoader } from './ultitranslateloader';
import { CurrencyComponent } from './components/currency/currency.component';
import { ClickOutsideDirective } from './directives/click-outside/click-outside.directive';
import { HogClickDirective } from './directives/hog-click/hog-click.directive';
import { IndeterminateDirective } from './directives/indeterminate/indeterminate.directive';
import { ConfigModule } from './services/config/config.module';
import { FeatureToggleModule } from './services/feature-toggle/feature-toggle.module';
import { HttpErrorInterceptor } from './interceptors/http-error.interceptor';
import { DateFormatHelper } from './components/date-picker/date-format-helper';
import { SettingsService } from './services/settings/settings.service';
import { CommonService } from './services/common/common.service';
import { TruncatePipe } from './pipes/truncate.pipe';
import {FeinFormatterPipe} from './pipes/fein-format.pipe';
import { SideFilterComponent } from './components/side-filter/side-filter.component';
import { NotificationBarComponent } from './components/notification-bar/notification-bar.component';
import { LoadingSpinnerComponent } from './components/loading-spinner/loading-spinner.component';
import { LoadingSpinnerService } from './services/loading-spinner/loading-spinner.service';
import { HttpLoadingInterceptor } from './interceptors/loading.interceptor';
import { FileExportService } from './services/export-file/export-file.service';
import { ScrollToTopComponent } from './components/scroll-to-top/scroll-to-top.component';
import { DateEstPipe } from './pipes/date-est.pipe';
import { EstNamePipe } from './pipes/est-name.pipe';
import { PendoComponent } from './components/pendo/pendo.component';

@Injectable()
export class SsgPopoverConfig extends NgbPopoverConfig {
  placement = 'right';
  mode = 'toggle';
  container = 'body';
  constructor() {
    super();
    this.animation = false;
  }
}

@NgModule({
    imports: [
        CommonModule,
        FormsModule,
        BrowserAnimationsModule,
        NgSelectModule,
        IgniteModule.forRoot(),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useClass: UltiTranslateLoader
            }
        }),
        NgbModule,
        IgniteModule.forRoot()
    ],
    declarations: [
        UltiCurrencyPipe,
        UltiShortDatePipe,
        DateEstPipe,
        EstNamePipe,
        TranslateListPipe,
        KeysPipe,
        EnumTranslatePipe,
        IsMinDatePipe,
        TruncatePipe,
        FeinFormatterPipe,
        VerticalProgressMapComponent,
        VerticalProgressStepComponent,
        ExpanderComponent,
        CurrencyComponent,
        DatePickerComponent,
        ClickOutsideDirective,
        HogClickDirective,
        IndeterminateDirective,
        SideFilterComponent,
        NotificationBarComponent,
        LoadingSpinnerComponent,
        ScrollToTopComponent,
        MultiSelectListItemTemplateDirective,
        MultiSelectSelectedItemTemplateDirective,
        MultiSelectComponent,
        MultiTextComponent,
        PendoComponent
    ],
    providers: [
        DatePipe,
        CurrencyPipe,
        TranslateListPipe,
        FeinFormatterPipe,
        TruncatePipe,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpErrorInterceptor,
            multi: true
        },
        { provide: HTTP_INTERCEPTORS, useClass: HttpLoadingInterceptor, multi: true },
        DateFormatHelper,
        CommonService,
        SettingsService,
        LoadingSpinnerService,
        FileExportService,
        EnumTranslatePipe,
        UltiShortDatePipe,
        UltiCurrencyPipe,
        {
            provide: NgbPopoverConfig,
            useClass: SsgPopoverConfig
        }
    ],
    exports: [
        UltiCurrencyPipe,
        UltiShortDatePipe,
        DateEstPipe,
        EstNamePipe,
        KeysPipe,
        EnumTranslatePipe,
        IsMinDatePipe,
        CurrencyComponent,
        TranslateListPipe,
        TruncatePipe,
        FeinFormatterPipe,
        VerticalProgressMapComponent,
        VerticalProgressStepComponent,
        ExpanderComponent,
        NgbModule,
        MultiSelectComponent,
        MultiTextComponent,
        DatePickerComponent,
        MultiSelectListItemTemplateDirective,
        MultiSelectSelectedItemTemplateDirective,
        ClickOutsideDirective,
        HogClickDirective,
        IndeterminateDirective,
        SideFilterComponent,
        NotificationBarComponent,
        TranslateModule,
        ConfigModule,
        FeatureToggleModule,
        HttpClientModule,
        LoadingSpinnerComponent,
        ScrollToTopComponent,
        PendoComponent
    ]
})
export class SharedModule {

  constructor(@Inject(LOCALE_ID) protected localeId: string, translate: TranslateService) {
    translate.setDefaultLang('en-US');
  }
}
